import React, { useState, useEffect, useMemo } from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import posthog from 'posthog-js';
import { API_URL } from '../../config';
import { mergeSubscriptionTier } from '../../utils/subscription';
import { showSuccess } from '../../utils/toast';
import '../../App.css';

const OPEN_RYNN_EVENT = 'sovrynn-open-rynn';
const AMBER_SCORE = '#E09B1A';

const CompleteStep = ({ onboardingData, userId }) => {
  const navigate = useNavigate();
  const [driScore, setDriScore] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCompleting, setIsCompleting] = useState(false);

  const hasPhone = useMemo(() => {
    const fromState =
      onboardingData.phone && String(onboardingData.phone).trim().length > 0;
    if (fromState) return true;
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return false;
      const u = JSON.parse(raw);
      return !!(u.phone && String(u.phone).trim().length > 0);
    } catch {
      return false;
    }
  }, [onboardingData.phone]);

  useEffect(() => {
    fetchDRIScore();
  }, []);

  const fetchDRIScore = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/dri/score`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDriScore(response.data?.totalScore ?? 0);
    } catch (error) {
      console.error('Error fetching DRI score:', error);
      setDriScore(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      const token = localStorage.getItem('token');

      await axios.post(
        `${API_URL}/api/users/${userId}/complete-onboarding`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        user.onboardingCompleted = true;
        localStorage.setItem('user', JSON.stringify(mergeSubscriptionTier(user)));
      }

      posthog.capture('onboarding_completed', {
        plan: onboardingData.selectedPlan,
        vehicleAdded: !!onboardingData.vehicleAdded,
        medicalCardUploaded: !!onboardingData.medicalCardUploaded,
      });

      showSuccess('Welcome to Sovrynn!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      navigate('/dashboard');
    } finally {
      setIsCompleting(false);
    }
  };

  const openRynn = () => {
    window.dispatchEvent(new CustomEvent(OPEN_RYNN_EVENT));
  };

  const checklistRows = useMemo(() => {
    const rows = [
      { key: 'profile', type: 'done', text: 'Profile created' },
    ];
    if (onboardingData.vehicleAdded) {
      rows.push({ key: 'vehicle', type: 'done', text: 'Vehicle added' });
    }
    if (onboardingData.medicalCardUploaded) {
      rows.push({
        key: 'medical',
        type: 'done',
        text: 'Medical card uploaded',
      });
    }
    rows.push({ key: 'plan', type: 'done', text: 'Plan selected' });
    if (hasPhone) {
      rows.push({
        key: 'sms',
        type: 'done',
        text: 'SMS alerts enabled',
      });
    } else {
      rows.push({
        key: 'sms-pending',
        type: 'muted',
        text:
          '— Add your mobile number in Settings to enable SMS alerts',
      });
    }
    return rows;
  }, [
    onboardingData.vehicleAdded,
    onboardingData.medicalCardUploaded,
    hasPhone,
  ]);

  const scoreDisplay = driScore ?? 0;

  return (
    <div className="onboarding-content">
      <div className="completion-animation">
        <div className="success-checkmark"><Check size={48} /></div>
        <h2 className="completion-title">You&apos;re road-ready.</h2>
        <p className="completion-subtitle">
          Here&apos;s where your operation stands today. Every item you complete
          improves your DRI Score™.
        </p>

        {!isLoading && (
          <div className="dri-score-display">
            <div
              className="score-circle"
              style={{
                borderColor: AMBER_SCORE,
                color: AMBER_SCORE,
              }}
            >
              <div className="score-value">{scoreDisplay}</div>
              <div className="score-label">/100</div>
            </div>
            <p className="score-description">
              Your DRI Score™ is {scoreDisplay}/100. Visit your Audit Readiness
              page to see exactly what to fix first.
            </p>
          </div>
        )}

        <div className="completion-checklist">
          <h3>What you&apos;ve completed:</h3>
          <ul>
            {checklistRows.map((row) => (
              <li
                key={row.key}
                className={row.type === 'muted' ? 'completion-checklist-muted' : ''}
              >
                {row.type === 'done' && (
                  <span className="completion-check-icon">
                    <Check size={18} />
                  </span>
                )}
                {row.type === 'muted' && (
                  <span className="completion-check-placeholder" aria-hidden="true" />
                )}
                <span>{row.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="completion-feature-highlight completion-rynn-card">
          <h3>
            Meet Rynn — Your Compliance Intelligence Director
          </h3>
          <p>
            The compliance director independent operators never had. Powered by Claude,
            Rynn knows FMCSA regulations inside out and answers in plain language,
            always available, never billing by the hour.
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={openRynn}
          >
            Ask Rynn Now
          </button>
        </div>

        <button
          className="btn btn-primary btn-large"
          onClick={handleComplete}
          disabled={isCompleting}
          style={{ marginTop: '30px', padding: '15px 40px', fontSize: '1.2rem' }}
        >
          {isCompleting ? 'Completing...' : 'Go to Dashboard →'}
        </button>
      </div>
    </div>
  );
};

export default CompleteStep;
